import { Unpacked } from '@givebacks/utils/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useStoreApi } from '../../api';
import { storeKeys } from '../../query-key-factory';
import { PeerParticipantActivityLogEntry, PeerTypes } from '../../types/peers';
import {
  PeerBulkFulfillAllEligiblePrizesRequest,
  PeerBulkFulfillPrizesRequest,
  PeersResponse,
  UpdatePeerRequest,
} from './types';
import { constructPeerFormData } from './utils';

const useUpdateCampaignPeer = <T extends PeerTypes>({ peerType }: { peerType: T }) => {
  const StoreApi = useStoreApi();
  const queryClient = useQueryClient();

  const updatePeer = async ({ uuid, causeId, campaignId, ...rest }: UpdatePeerRequest<T>) => {
    if (!causeId || !campaignId || !uuid) throw new Error('Missing required value');

    const form = constructPeerFormData<T>({ causeId, campaignId, peerType, uuid, request: rest });

    const { data } = await StoreApi.put<PeersResponse<T>>(
      `/store_items/${campaignId}/${peerType}/${uuid}`,
      form
    );
    return data[peerType == 'participants' ? 'participant' : 'team'];
  };
  return useMutation({
    mutationFn: updatePeer,
    onSuccess(_, variables) {
      const { causeId, campaignId } = variables;
      const key =
        campaignId && causeId
          ? storeKeys.id(causeId).items.campaigns.id(campaignId)[peerType].all
          : undefined;
      return queryClient.invalidateQueries({ queryKey: key });
    },
  });
};

/** Expected Participant[] Response*/
type ParticipantResponse = PeersResponse<'participants'>['participants'];
/** Participant with error[] optionally attached */
type ParticipantWithError = Unpacked<ParticipantResponse> & {
  errors: string[];
};
interface ImportParticipantResponse extends PeersResponse<'participants'> {
  participants: ParticipantWithError[];
}

/**
 * Custom hook for importing bulk participant activity.
 *
 * This hook is used to import bulk participant activity.
 * It makes a POST request to the store API to update the activity log of participants.
 */
const useImportBulkParticipantActivity = () => {
  const StoreApi = useStoreApi();
  const queryClient = useQueryClient();

  const importBulkParticipantActivity = async ({
    causeId,
    campaignId,
    activityLog,
  }: {
    causeId: string;
    campaignId: string;
    activityLog: {
      date?: string;
      value: number;
      uuid: string;
    }[];
  }) => {
    if (!causeId || !campaignId) throw new Error('Missing required value');

    const { data } = await StoreApi.post<ImportParticipantResponse>(
      `/store_items/${campaignId}/participants/bulk_activity`,
      {
        cause_id: causeId,
        activity_log: activityLog,
      }
    );
    return data;
  };

  return useMutation({
    mutationFn: importBulkParticipantActivity,
    onSuccess(_, variables) {
      const { causeId, campaignId } = variables;
      const key = storeKeys.id(causeId).items.campaigns.id(campaignId).participants.all;

      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
};
const useUpdateParticipantActivityLog = () => {
  const StoreApi = useStoreApi();
  const queryClient = useQueryClient();

  const updateActivityLog = async ({
    participantId,
    causeId,
    campaignId,
    activityLog,
  }: {
    participantId: string;
    causeId: string;
    campaignId: string;
    activityLog: PeerParticipantActivityLogEntry[];
  }) => {
    if (!causeId || !campaignId || !participantId) throw new Error('Missing required value');
    const { data } = await StoreApi.put<PeersResponse<'participants'>>(
      `/store_items/${campaignId}/participants/${participantId}`,
      {
        participant: {
          activity_log: activityLog,
        },
        cause_id: causeId,
      }
    );
    return data.participants;
  };

  return useMutation({
    mutationFn: updateActivityLog,
    onSuccess(_, variables) {
      const { causeId, campaignId } = variables;
      const key = storeKeys.id(causeId).items.campaigns.id(campaignId).participants.all;

      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
};

const useBulkFulfillPeerPrizes = <T extends PeerTypes>({ peerType }: { peerType: T }) => {
  const StoreApi = useStoreApi();
  const queryClient = useQueryClient();

  const bulkFulfillPeerPrizes = async ({
    causeId,
    campaignId,
    peerIds,
    prizeIds,
  }: PeerBulkFulfillPrizesRequest) => {
    const { data } = await StoreApi.put<PeersResponse<T>>(
      `/store_items/${campaignId}/${peerType}/fulfill_prizes`,
      { cause_id: causeId, store_item_id: campaignId, peer_page_ids: peerIds, prize_ids: prizeIds }
    );
    return data;
  };

  return useMutation({
    mutationFn: bulkFulfillPeerPrizes,
    onSuccess(_, variables) {
      const { causeId, campaignId } = variables;
      const key = storeKeys.id(causeId).items.campaigns.id(campaignId)[peerType].all;

      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
};

const useBulkUnFulfillPeerPrizes = <T extends PeerTypes>({ peerType }: { peerType: T }) => {
  const StoreApi = useStoreApi();
  const queryClient = useQueryClient();

  const bulkUnFulfillPeerPrizes = async ({
    causeId,
    campaignId,
    peerIds,
    prizeIds,
  }: PeerBulkFulfillPrizesRequest) => {
    const { data } = await StoreApi.put<PeersResponse<T>>(
      `/store_items/${campaignId}/${peerType}/unfulfill_prizes`,
      { cause_id: causeId, store_item_id: campaignId, peer_page_ids: peerIds, prize_ids: prizeIds }
    );
    return data;
  };

  return useMutation({
    mutationFn: bulkUnFulfillPeerPrizes,
    onSuccess(_, variables) {
      const { causeId, campaignId } = variables;
      const key = storeKeys.id(causeId).items.campaigns.id(campaignId)[peerType].all;

      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
};

/** Bulk fulfill all eligible prizes for a peers */
const useBulkFulfillAllEligiblePrizes = <T extends PeerTypes>({ peerType }: { peerType: T }) => {
  const StoreApi = useStoreApi();
  const queryClient = useQueryClient();

  const bulkFulfillAllEligiblePrizes = async ({
    causeId,
    campaignId,
    prizeIds,
  }: PeerBulkFulfillAllEligiblePrizesRequest) => {
    const { data } = await StoreApi.put<PeersResponse<T>>(
      `/store_items/${campaignId}/prizes/bulk_fulfill_peer_pages`,
      { cause_id: causeId, store_item_id: campaignId, prize_ids: prizeIds }
    );
    return data;
  };

  return useMutation({
    mutationFn: bulkFulfillAllEligiblePrizes,
    onSuccess(_, variables) {
      const { causeId, campaignId } = variables;
      const key = storeKeys.id(causeId).items.campaigns.id(campaignId)[peerType].all;

      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
};

export {
  useBulkFulfillAllEligiblePrizes,
  useBulkFulfillPeerPrizes,
  useBulkUnFulfillPeerPrizes,
  useImportBulkParticipantActivity,
  useUpdateCampaignPeer,
  useUpdateParticipantActivityLog,
};
export type { ImportParticipantResponse };
